import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarPlus, CheckCircle, XCircle, ArrowRight, ArrowLeft, Search, Download, RefreshCcw, LogOut, ArrowUpDown, MessageCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { perfilNoModulo } from '../../../utils/perfisDerivados';

const ProcessoSeletivoEntrevistaAgendamento = () => {
    const [candidatos, setCandidatos] = useState([]);
    const [escalasDb, setEscalasDb] = useState([]); 
    const [gruposCargos, setGruposCargos] = useState([]); 
    const [isLoading, setIsLoading] = useState(true);

    // Estado para a Super Pop-up de Automação
    const [automacaoModal, setAutomacaoModal] = useState({
        isOpen: false,
        titulo: '',
        mensagem: '',
        onConfirm: null, // Função se clicar em SIM
        onCancel: null   // Função se clicar em NÃO
    });

    const [subAbaAtiva, setSubAbaAtiva] = useState('agendar');
    const [agendamentos, setAgendamentos] = useState({});
    
    const [candidatosSelecionados, setCandidatosSelecionados] = useState([]);
    const [bulkAgendamento, setBulkAgendamento] = useState({ local: '', data: '' });
    
    const [filterCpf, setFilterCpf] = useState('');
    const [filterNome, setFilterNome] = useState('');
    const [filterVaga, setFilterVaga] = useState('');
    const [filterStatusAgendar, setFilterStatusAgendar] = useState('TODOS'); 
    const [filterDataInscricao, setFilterDataInscricao] = useState({ inicio: '', fim: '' });
    const [filterLocal, setFilterLocal] = useState(''); 

    // Ao abrir, já vem ordenado por Status (decrescente): Parecer → Faltou → Retorno → Novo.
    const [sortConfig, setSortConfig] = useState({ key: 'STATUS', direction: 'desc' });

    // Paginação (50 por página)
    const [paginaAtual, setPaginaAtual] = useState(1);
    const ITENS_POR_PAGINA = 50;

    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : {};
    const role = perfilNoModulo(user.perfil, 'processoseletivo');
    const canAction = role === 'Admin' || role === 'Gerente' || role === 'RH' || role === 'ProcessoSeletivo' || role === 'Diretoria';
    const hasFixedLoja = user.loja && user.loja !== 'null' && user.loja.trim() !== '';

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalStep, setModalStep] = useState(1);
    const [candidatoSelecionado, setCandidatoSelecionado] = useState(null);
    
    const [vagaData, setVagaData] = useState({ 
        nome: '', cpf: '', email: '', celular: '', grupo: '', cargo: '', tipo_escala: '', nome_escala: '', folga: '', loja: '', data_exame: '', hora_exame: '08:00'
    });

    const getNextWeekdays = (targetDay) => { 
        const days = [];
        let d = new Date(); d.setHours(0, 0, 0, 0); 
        let diff = (targetDay - d.getDay() + 7) % 7; 
        d.setDate(d.getDate() + diff);
        for (let i = -1; i < 4; i++) {
            let nextD = new Date(d); nextD.setDate(d.getDate() + (i * 7));
            days.push(nextD);
        }
        return days;
    };
    
    const proximasSegundas = getNextWeekdays(1);
    const proximasQuartas = getNextWeekdays(3);
    const proximasSextas = getNextWeekdays(5);
    // Mescla as segundas e quartas e ordena cronologicamente
    const proximasSegundasEQuartas = [...proximasSegundas, ...proximasQuartas].sort((a, b) => a - b);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

                const [resCandidatos, resEscalas, resGrupos] = await Promise.all([
                    fetch('/api/processo-seletivo/candidatos', { headers }),
                    fetch('/api/processo-seletivo/escalas', { headers }), 
                    fetch('/api/processo-seletivo/grupos-cargos', { headers })
                ]);

                if (!resCandidatos.ok || !resEscalas.ok || !resGrupos.ok) throw new Error("Erro ao carregar os dados.");

                setCandidatos(await resCandidatos.json());
                setEscalasDb(await resEscalas.json());
                setGruposCargos(await resGrupos.json());
                setIsLoading(false);
            } catch (err) {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    // Qualquer mudança de filtro/chip/ordenação volta para a 1ª página.
    useEffect(() => { setPaginaAtual(1); }, [filterStatusAgendar, filterNome, filterCpf, filterVaga, filterLocal, filterDataInscricao, sortConfig]);

    const candidatosAgendar = candidatos.filter(c => c.ORIGEM === 'Processo' && c.STATUS_ETAPA === 2);
    const candidatosParecer = candidatos.filter(c => c.ORIGEM === 'Processo' && c.STATUS_ETAPA === 3);
    const listaAtual = candidatos.filter(c => c.ORIGEM === 'Processo' && (c.STATUS_ETAPA === 2 || c.STATUS_ETAPA === 3));

    const vagasDisponiveis = [...new Set(listaAtual.map(c => c.VAGA_SELECIONADA).filter(Boolean))].sort();

    // Categoria de status na fila de um candidato (para chips + ordenação).
    const statusFila = (cand) => {
        if (cand.STATUS_ETAPA === 3) return 'PARECER';
        if (cand.MOTIVO_REPROVACAO === 'REMARCADO') return 'FALTOU';
        if (cand.MOTIVO_REPROVACAO === 'AGUARDANDO_RETORNO') return 'RETORNO';
        return 'NOVO';
    };

    // 1) Filtra por TUDO menos o status (nome, cpf, vaga, período, local) — base das contagens.
    const baseFiltrada = listaAtual.filter((cand) => {
        const matchCpf = cand.CPF?.replace(/\D/g, '').includes(filterCpf.replace(/\D/g, ''));
        const matchNome = cand.NOME_COMPLETO?.toLowerCase().includes(filterNome.toLowerCase());
        const matchVaga = filterVaga === '' || cand.VAGA_SELECIONADA === filterVaga;
        // Conserto: o filtro de Local antes era ignorado (matchLocal ficava sempre true).
        // 'SEM_LOCAL' = candidatos sem local definido (em geral os ainda não agendados).
        const matchLocal = filterLocal === ''
            || (filterLocal === 'SEM_LOCAL' ? !cand.LOCAL_ENTREVISTA : cand.LOCAL_ENTREVISTA === filterLocal);

        let matchData = true;
        if (filterDataInscricao.inicio || filterDataInscricao.fim) {
            const candDataInsc = cand.DATA_INSCRICAO ? cand.DATA_INSCRICAO.substring(0, 10) : '';
            if (!candDataInsc) {
                matchData = false;
            } else if (filterDataInscricao.inicio && filterDataInscricao.fim) {
                matchData = candDataInsc >= filterDataInscricao.inicio && candDataInsc <= filterDataInscricao.fim;
            } else if (filterDataInscricao.inicio) {
                matchData = candDataInsc >= filterDataInscricao.inicio;
            } else if (filterDataInscricao.fim) {
                matchData = candDataInsc <= filterDataInscricao.fim;
            }
        }

        return matchCpf && matchNome && matchVaga && matchData && matchLocal;
    });

    // Contagens por status (sobre a base já filtrada) — alimentam os chips clicáveis.
    const contagens = {
        TODOS: baseFiltrada.length,
        NOVO: baseFiltrada.filter(c => statusFila(c) === 'NOVO').length,
        PARECER: baseFiltrada.filter(c => statusFila(c) === 'PARECER').length,
        FALTOU: baseFiltrada.filter(c => statusFila(c) === 'FALTOU').length,
        RETORNO: baseFiltrada.filter(c => statusFila(c) === 'RETORNO').length,
    };

    // 2) Aplica o chip de status selecionado.
    let candidatosFiltrados = filterStatusAgendar === 'TODOS'
        ? [...baseFiltrada]
        : baseFiltrada.filter(c => statusFila(c) === filterStatusAgendar);

    if (sortConfig.key) {
        // Ordem lógica do status na fila (Novo → Retorno → Faltou → Parecer)
        const ordemStatus = { NOVO: 0, RETORNO: 1, FALTOU: 2, PARECER: 3 };
        candidatosFiltrados.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (sortConfig.key === 'CANDIDATO_VAGA') {
                aValue = a.NOME_COMPLETO?.toLowerCase() || '';
                bValue = b.NOME_COMPLETO?.toLowerCase() || '';
            } else if (sortConfig.key === 'STATUS') {
                aValue = ordemStatus[statusFila(a)];
                bValue = ordemStatus[statusFila(b)];
            }

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) setCandidatosSelecionados(candidatosFiltrados.map(c => c.ID));
        else setCandidatosSelecionados([]);
    };

    const handleSelectCandidate = (id) => {
        setCandidatosSelecionados(prev => prev.includes(id) ? prev.filter(candId => candId !== id) : [...prev, id]);
    };

    const handleToggleContato = async (idCandidato, isAguardando) => {
        try {
            const token = localStorage.getItem('authToken');
            await fetch('/api/processo-seletivo/marcar-contato', {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ idCandidato, flag: isAguardando })
            });
            setCandidatos(prev => prev.map(c => c.ID === idCandidato ? { ...c, MOTIVO_REPROVACAO: isAguardando ? 'AGUARDANDO_RETORNO' : null } : c));
        } catch (error) { alert("Erro ao atualizar o status de contato."); }
    };

    const handleBulkToggleContato = async () => {
        if (!window.confirm(`Deseja marcar ${candidatosSelecionados.length} candidato(s) como 'Aguardando Retorno'?`)) return;
        try {
            const token = localStorage.getItem('authToken');
            await Promise.all(candidatosSelecionados.map(id => fetch('/api/processo-seletivo/marcar-contato', {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ idCandidato: id, flag: true })
            })));
            setCandidatos(prev => prev.map(c => candidatosSelecionados.includes(c.ID) ? { ...c, MOTIVO_REPROVACAO: 'AGUARDANDO_RETORNO' } : c));
            setCandidatosSelecionados([]);
            alert("Candidatos marcados com sucesso!");
        } catch (error) { alert("Erro ao atualizar em massa."); }
    };

    const handleBulkRegistrarDesistencia = async () => {
        if (candidatosSelecionados.length === 0) return;
        
        if (!window.confirm(`Deseja registrar a desistência de ${candidatosSelecionados.length} candidato(s)? Esta ação os moverá para o módulo de Desistentes.`)) return;

        try {
            const token = localStorage.getItem('authToken');
            await Promise.all(candidatosSelecionados.map(id => 
                fetch('/api/processo-seletivo/registrar-desistencia', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ idCandidato: id })
                })
            ));

            setCandidatos(prev => prev.filter(c => !candidatosSelecionados.includes(c.ID)));
            setCandidatosSelecionados([]);
            alert("Candidatos movidos para o módulo de Desistentes com sucesso!");
        } catch (error) {
            alert("Erro ao registrar desistência em massa.");
        }
    };

    const handleBulkFaltaRepescagem = async () => {
        if (candidatosSelecionados.length === 0) return;
        
        const confirmarFaltaLote = window.confirm(`Tem certeza que deseja marcar ${candidatosSelecionados.length} candidato(s) como Faltou/Remarcar? Eles retornarão para a Fila de Agendamento.`);
        if (!confirmarFaltaLote) return;

        const executarFaltaLote = async (enviarMensagem) => {
            setAutomacaoModal({ ...automacaoModal, isOpen: false });

            try {
                const token = localStorage.getItem('authToken');
                await Promise.all(candidatosSelecionados.map(id => 
                    fetch('/api/processo-seletivo/retornar-fila-entrevista', { 
                        method: 'POST', 
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, 
                        body: JSON.stringify({ idCandidato: id, enviar_automacao: enviarMensagem }) 
                    })
                ));

                setCandidatos(prev => prev.map(c => 
                    candidatosSelecionados.includes(c.ID) 
                        ? { ...c, STATUS_ETAPA: 2, LOCAL_ENTREVISTA: null, DATA_HORA_ENTREVISTA: null, MOTIVO_REPROVACAO: 'REMARCADO' } 
                        : c
                ));
                setCandidatosSelecionados([]);
                alert("Candidatos retornados à fila com sucesso!");
            } catch (error) { 
                alert("Erro ao retornar candidatos em lote."); 
            }
        };

        setAutomacaoModal({
            isOpen: true,
            titulo: 'Disparar Automação em Lote? 🤖',
            mensagem: `Deseja enviar a mensagem automática de repescagem (WhatsApp) para TODOS os ${candidatosSelecionados.length} candidatos selecionados?`,
            onConfirm: () => executarFaltaLote(true),
            onCancel: () => executarFaltaLote(false)
        });
    };

    const handleBulkAgendamento = async () => {
        if (!bulkAgendamento.local || !bulkAgendamento.data) return alert("Por favor, selecione o Local e a Data para agendar em massa.");
        
        // ETAPA 1: Lógica dinâmica de horário. PCI: Seg 9h / Qua 14h. GTV: 14h.
        const dataSelecionadaObj = new Date(bulkAgendamento.data + 'T00:00:00');
        const diaDaSemana = dataSelecionadaObj.getDay(); // 1 = Segunda, 3 = Quarta
        
        let timeStr = '14:00:00';
        if (bulkAgendamento.local === 'PCI') {
            timeStr = diaDaSemana === 1 ? '09:00:00' : '14:00:00';
        }
        
        const data_hora = `${bulkAgendamento.data} ${timeStr}`;
        const token = localStorage.getItem('authToken');

        try {
            await Promise.all(candidatosSelecionados.map(id => fetch('/api/processo-seletivo/agendar-entrevista', {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ idCandidato: id, local: bulkAgendamento.local, data_hora })
            })));
            alert(`Agendamento confirmado para ${candidatosSelecionados.length} candidato(s)!`);
            setCandidatos(prev => prev.map(c => candidatosSelecionados.includes(c.ID) ? { ...c, STATUS_ETAPA: 3, LOCAL_ENTREVISTA: bulkAgendamento.local, DATA_HORA_ENTREVISTA: data_hora, MOTIVO_REPROVACAO: null } : c));
            setCandidatosSelecionados([]);
            setBulkAgendamento({ local: '', data: '' });
        } catch (error) { alert("Erro ao salvar agendamentos em massa."); }
    };

    const handleAgendamentoChange = (id, campo, valor) => setAgendamentos(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [campo]: valor } }));

    const handleSalvarAgendamento = async (idCandidato) => {
        const cand = candidatos.find(c => c.ID === idCandidato);
        const local = agendamentos[idCandidato]?.local !== undefined ? agendamentos[idCandidato].local : cand.LOCAL_ENTREVISTA;
        const dataSelecionada = agendamentos[idCandidato]?.data !== undefined ? agendamentos[idCandidato].data : (cand.DATA_HORA_ENTREVISTA ? cand.DATA_HORA_ENTREVISTA.substring(0,10) : '');

        if (!local || !dataSelecionada) return alert("Por favor, selecione o Local e a Data da Entrevista.");
        
        // ETAPA 2: Lógica dinâmica de horário para agendamento individual
        const dataSelecionadaObj = new Date(dataSelecionada + 'T00:00:00');
        const diaSemanaIndividual = dataSelecionadaObj.getDay();
        
        let timeStr = '14:00:00';
        if (local === 'PCI') {
            timeStr = diaSemanaIndividual === 1 ? '09:00:00' : '14:00:00';
        }
        
        const data_hora = `${dataSelecionada} ${timeStr}`;

        try {
            const token = localStorage.getItem('authToken');
            await fetch('/api/processo-seletivo/agendar-entrevista', {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ idCandidato, local, data_hora })
            });
            alert("Agendamento confirmado! Candidato movido para a aba 'Aguardando Parecer'.");
            setCandidatos(prev => prev.map(c => c.ID === idCandidato ? { ...c, STATUS_ETAPA: 3, LOCAL_ENTREVISTA: local, DATA_HORA_ENTREVISTA: data_hora, MOTIVO_REPROVACAO: null } : c));
        } catch (error) { alert("Erro ao salvar agendamento."); }
    };

    const abrirModalAprovacao = (candidato) => {
        setCandidatoSelecionado(candidato);
        setModalStep(1); 
        let grupoSugerido = ''; let cargoSugerido = '';
        const matchCargo = gruposCargos.find(gc => gc.NOME_CARGO_ABREV === candidato.VAGA_SELECIONADA);
        if (matchCargo) { grupoSugerido = matchCargo.SUB_GRUPO_ABREV; cargoSugerido = matchCargo.NOME_CARGO_ABREV; }

        const isEntrevistaGTV = candidato.LOCAL_ENTREVISTA === 'GTV';

        setVagaData({
            nome: candidato.NOME_COMPLETO || '', cpf: candidato.CPF || '', email: candidato.EMAIL || '', celular: candidato.CELULAR || '',
            grupo: grupoSugerido, cargo: cargoSugerido, tipo_escala: '', nome_escala: '', folga: '',
            loja: isEntrevistaGTV ? 'GTV' : (hasFixedLoja ? user.loja : ''),
            data_exame: '', hora_exame: '08:00'
        });
        setIsModalOpen(true);
    };

    const isGTV = candidatoSelecionado?.LOCAL_ENTREVISTA === 'GTV';

    const handleAvancarParaExame = () => {
        if (!vagaData.nome || !vagaData.cpf || !vagaData.email || !vagaData.celular) return alert("Os dados pessoais do candidato não podem ficar vazios.");
        if (!vagaData.grupo || !vagaData.cargo || !vagaData.tipo_escala || !vagaData.nome_escala || !vagaData.folga) return alert("Preencha todos os campos da contratação (Escala, Horários e Folga)!");
        if (!vagaData.loja) return alert("Por favor, selecione a Loja de Atuação do candidato.");
        
        if (isGTV) handleAprovarSubmeter(true); 
        else setModalStep(2);
    };

    const handleAprovarSubmeter = async (isGTVBypass = false) => {
        const bypass = typeof isGTVBypass === 'boolean' ? isGTVBypass : false;
        if (!bypass && !vagaData.data_exame) return alert("Por favor, selecione a data do Exame Admissional.");
        const data_hora_exame_formatada = bypass ? null : `${vagaData.data_exame} 08:00:00`;

        // Função interna que dispara o banco de dados
        const executarAprovacao = async (enviarMensagem) => {
            // Fecha o modal gigante
            setAutomacaoModal({ ...automacaoModal, isOpen: false });

            try {
                const token = localStorage.getItem('authToken');
                const response = await fetch('/api/processo-seletivo/aprovar-entrevista', {
                    method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ 
                        idCandidato: candidatoSelecionado.ID, 
                        nome_completo: vagaData.nome, 
                        cpf: vagaData.cpf, 
                        email: vagaData.email, 
                        celular: vagaData.celular,
                        cargo: vagaData.cargo, 
                        tipo_escala: vagaData.tipo_escala, 
                        nome_escala: vagaData.nome_escala, 
                        folga: vagaData.folga,
                        loja_atuacao: vagaData.loja,
                        data_hora_exame: data_hora_exame_formatada,
                        pulaExame: bypass,
                        enviar_automacao: enviarMensagem
                        // Removido o 'origem' daqui, pois a rota não usa!
                    })
                });
                const data = await response.json();
                if (!response.ok) {
                    alert("Erro: " + data.message); 
                    return;
                }

                alert(bypass ? "Sucesso! Candidato aprovado e movido diretamente para a Integração." : "Sucesso! Candidato aprovado, exame agendado e e-mail único disparado!");
                setIsModalOpen(false);
                
                setCandidatos(prevCandidatos => prevCandidatos.filter(cand => cand.ID !== candidatoSelecionado.ID));
            } catch (error) { 
                alert("Erro de conexão."); 
            }
        }; 

        // ABRE O MODAL GIGANTE
        setAutomacaoModal({
            isOpen: true,
            titulo: 'Aprovar e Enviar E-mail? 📧',
            mensagem: 'Deseja enviar o E-MAIL automático com as instruções do exame e próximos passos?',
            onConfirm: () => executarAprovacao(true), // Se clicar no Verde
            onCancel: () => executarAprovacao(false)  // Se clicar no Cinza
        });
    };

    const handleRegistrarDesistencia = async (idCandidato) => {
        if (!window.confirm("O candidato desistiu do processo? Esta ação o moverá para o módulo de Desistentes.")) return;
        try {
            const token = localStorage.getItem('authToken');
            await fetch('/api/processo-seletivo/registrar-desistencia', { 
                method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ idCandidato }) 
            });
            setCandidatos(prev => prev.filter(c => c.ID !== idCandidato));
            alert("Candidato movido para o módulo de Desistentes com a data de hoje.");
        } catch (error) { alert("Erro ao registrar a desistência."); }
    };

    const handleFaltaRepescagem = async (idCandidato) => {
        const confirmarFalta = window.confirm("O candidato faltou / não pode comparecer à entrevista. Deseja retorná-lo para a fila de agendamento (repescagem)?");
        if (!confirmarFalta) return;
        
        // Função interna que fará o disparo real para o Backend
        const executarFaltaRepescagem = async (enviarMensagem) => {
            // Fecha o modal gigante
            setAutomacaoModal({ ...automacaoModal, isOpen: false });

            try {
                const token = localStorage.getItem('authToken');
                const response = await fetch('/api/processo-seletivo/retornar-fila-entrevista', { 
                    method: 'POST', 
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, 
                    body: JSON.stringify({ idCandidato, enviar_automacao: enviarMensagem })
                });
                
                const data = await response.json();
                if (!response.ok) {
                    alert("Erro: " + data.message); 
                    return;
                }
                
                setCandidatos(prevCandidatos => prevCandidatos.map(cand => cand.ID === idCandidato ? {...cand, STATUS_ETAPA: 2, LOCAL_ENTREVISTA: null, DATA_HORA_ENTREVISTA: null, MOTIVO_REPROVACAO: 'REMARCADO'} : cand));
            } catch (error) { 
                alert("Erro de conexão ao retornar candidato."); 
            }
        }; 

        // ABRE O MODAL GIGANTE
        setAutomacaoModal({
            isOpen: true,
            titulo: 'Disparar Automação? 🤖',
            mensagem: 'Deseja enviar a mensagem automática (WhatsApp) avisando o candidato sobre a repescagem?',
            onConfirm: () => executarFaltaRepescagem(true),
            onCancel: () => executarFaltaRepescagem(false)  
        });
    };

    const handleReprovarEntrevista = async (idCandidato) => {
        if (!window.confirm("Tem certeza que deseja reprovar este candidato por não adequação à cultura da empresa?")) return;
        try {
            const token = localStorage.getItem('authToken');
            await fetch('/api/processo-seletivo/reprovar', { 
                method: 'POST', 
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, 
                body: JSON.stringify({ idCandidato, motivo: "Não se adequa a cultura da empresa", origem: 'Processo' }) 
            });
            setCandidatos(prev => prev.filter(c => c.ID !== idCandidato));
            alert("Candidato reprovado com sucesso.");
        } catch (error) { 
            alert("Erro ao reprovar."); 
        }
    };

    const exportarExcel = () => {
        const dataToExport = candidatosFiltrados.map(cand => {
            // 1. Formata a data de inscrição do candidato atual (DENTRO do map)
            let dataInscFormatada = 'Não definida';
            if (cand.DATA_INSCRICAO) {
                const dObj = new Date(cand.DATA_INSCRICAO);
                dataInscFormatada = dObj.toLocaleDateString('pt-BR');
            }

            // 2. Formata a data e hora da entrevista
            let dataFormatada = 'Não definida'; 
            let horaFormatada = '-'; 
            let dataHoraConcatenada = 'Não definida,-';
            
            if (cand.DATA_HORA_ENTREVISTA) {
                const dObj = new Date(cand.DATA_HORA_ENTREVISTA);
                dataFormatada = dObj.toLocaleDateString('pt-BR');
                horaFormatada = dObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                dataHoraConcatenada = `${dataFormatada},${horaFormatada}`;
            }
            
            // 3. Monta a linha do Excel, com Data da Inscrição primeiro
            const rowData = {
                'Data da Inscrição': dataInscFormatada,
                'Nome Completo': cand.NOME_COMPLETO, 
                'CPF': cand.CPF.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4"),
                'Celular': cand.CELULAR, 
                'Vaga Selecionada': cand.VAGA_SELECIONADA || 'Não informada',
                'Local da Entrevista': cand.LOCAL_ENTREVISTA || 'Não definido', 
                'Data da Entrevista': dataFormatada,
                'Horário': horaFormatada
            };
            
            if (cand.STATUS_ETAPA === 3) rowData['Data Entrevista/Horario'] = dataHoraConcatenada;

            const rotuloStatus = { NOVO: 'Novo Agendamento', PARECER: 'Aguardando Parecer', FALTOU: 'Faltou / Remarcar', RETORNO: 'Aguardando Retorno' };
            rowData['Status'] = rotuloStatus[statusFila(cand)];

            return rowData;
        });

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Entrevistas");
        XLSX.writeFile(workbook, `Controle_Entrevistas_${subAbaAtiva}.xlsx`);
    };

    const gruposUnicos = [...new Set(gruposCargos.map(g => g.SUB_GRUPO_ABREV))].sort();
    const cargosFiltrados = gruposCargos.filter(gc => gc.SUB_GRUPO_ABREV === vagaData.grupo).map(gc => gc.NOME_CARGO_ABREV).sort();
    const formatHora = (h) => h ? h.substring(0, 5) : '--:--';
    const escalasFiltradas = escalasDb.filter(e => e.tipo_turno && vagaData.tipo_escala && e.tipo_turno.toUpperCase() === vagaData.tipo_escala.toUpperCase());
    const horariosEscalaUnicos = [...new Set(escalasFiltradas.map(e => `${formatHora(e.hora_entrada1)} às ${formatHora(e.hora_saida2)}`))].sort();

    const renderFolgaOptions = () => {
        const escala = vagaData.tipo_escala.toUpperCase();
        if (escala === '12X36') return <><option value="ÍMPAR">Ímpar</option><option value="PAR">Par</option></>;
        if (escala === 'PARTIME') return <><option value="SEG e TER">SEG e TER</option><option value="TER e QUA">TER e QUA</option><option value="QUA e QUI">QUA e QUI</option><option value="QUI e SEG">QUI e SEG</option></>;
        if (escala === '5X2') return <><option value="SEG e TER">SEG e TER</option><option value="TER e QUA">TER e QUA</option><option value="QUA e QUI">QUA e QUI</option><option value="QUI e SEG">QUI e SEG</option><option value="SEG e QUA">SEG e QUA</option><option value="SEG e QUI">SEG e QUI</option></>;
        return null;
    };

    const inputStyleModal = { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontWeight: '600', color: '#0f172a', fontSize: '13px' };
    const labelStyleModal = { display: 'block', fontSize: '12px', fontWeight: '800', color: '#475569', marginBottom: '6px', textTransform: 'uppercase' };

    let diaDaEntrevista = -1;
    if (candidatoSelecionado && candidatoSelecionado.DATA_HORA_ENTREVISTA) {
        const dataStr = candidatoSelecionado.DATA_HORA_ENTREVISTA.substring(0, 10);
        const partes = dataStr.split('-');
        if (partes.length === 3) {
            const dataEntrevistaObj = new Date(partes[0], partes[1] - 1, partes[2]);
            diaDaEntrevista = dataEntrevistaObj.getDay();
        }
    }




    
    if (isLoading) return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Carregando dados...</div>;
    const isAllSelected = candidatosFiltrados.length > 0 && candidatosSelecionados.length === candidatosFiltrados.length;

    // Recorte da página atual (50 por vez)
    const totalPaginas = Math.ceil(candidatosFiltrados.length / ITENS_POR_PAGINA);
    const indexPrimeiro = (paginaAtual - 1) * ITENS_POR_PAGINA;
    const candidatosPaginados = candidatosFiltrados.slice(indexPrimeiro, indexPrimeiro + ITENS_POR_PAGINA);
    
    // ETAPA 3: Variáveis dinâmicas para o lote
    const diasBulkDisponiveis = bulkAgendamento.local === 'GTV' ? proximasSextas : proximasSegundasEQuartas;
    const horaBulkDisplay = bulkAgendamento.local === 'GTV' ? '14:00' : '14:00';

    // Calcula a data de 7 dias atrás para o limite do calendário
    const dataMinima = new Date();
    dataMinima.setDate(dataMinima.getDate() - 7);
    const dataMinimaFormatada = dataMinima.toISOString().split('T')[0];

    return (
    <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
        <div style={{ marginBottom: '24px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '900', color: '#0f172a', marginBottom: '8px', margin: 0 }}>Gestão de Entrevistas</h2>
            <p style={{ color: '#64748b', fontSize: '15px', margin: 0 }}>Agende horários e registre o parecer final dos candidatos na mesma tela.</p>
        </div>

        {/* CHIPS DE STATUS — clicáveis, com contagem. Substituem os antigos KPIs + o
            dropdown "Status na Fila" (eram redundantes). Clicar filtra a tabela. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '24px' }}>
            {[
                { key: 'TODOS', label: 'Todos', cor: '#475569' },
                { key: 'NOVO', label: 'Novo Agendamento', cor: '#166534' },
                { key: 'PARECER', label: 'Aguardando Parecer', cor: '#4338ca' },
                { key: 'FALTOU', label: 'Faltou / Remarcar', cor: '#b91c1c' },
                { key: 'RETORNO', label: 'Aguardando Retorno', cor: '#b45309' },
            ].map(chip => {
                const ativo = filterStatusAgendar === chip.key;
                return (
                    <button key={chip.key} onClick={() => setFilterStatusAgendar(chip.key)} style={{
                        display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 14px',
                        borderRadius: '999px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                        border: ativo ? '2px solid #e11d48' : '1px solid #e2e8f0',
                        backgroundColor: ativo ? '#ffe4e6' : '#fff',
                        color: ativo ? '#e11d48' : '#475569', transition: 'all 0.15s',
                    }}>
                        {chip.label}
                        <span style={{ backgroundColor: chip.cor, color: '#fff', borderRadius: '999px', padding: '1px 9px', fontSize: '12px', fontWeight: 900 }}>{contagens[chip.key]}</span>
                    </button>
                );
            })}
        </div>

        {/* FILTROS UNIFICADOS */}
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', marginBottom: '24px', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', color: '#475569', textTransform: 'uppercase', marginBottom: '8px' }}>Nome do Candidato</label>
                <div style={{ position: 'relative' }}>
                    <Search size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '12px' }} />
                    <input type="text" placeholder="Nome Completo..." value={filterNome} onChange={(e) => setFilterNome(e.target.value)} style={{ width: '100%', padding: '10px 10px 10px 36px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }} />
                </div>
            </div>
            <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', color: '#475569', textTransform: 'uppercase', marginBottom: '8px' }}>CPF</label>
                <div style={{ position: 'relative' }}>
                    <Search size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '12px' }} />
                    <input type="text" placeholder="Apenas números..." value={filterCpf} onChange={(e) => setFilterCpf(e.target.value.replace(/\D/g, ''))} style={{ width: '100%', padding: '10px 10px 10px 36px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }} />
                </div>
            </div>
            <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', color: '#475569', textTransform: 'uppercase', marginBottom: '8px' }}>Vaga Selecionada</label>
                <select value={filterVaga} onChange={(e) => setFilterVaga(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', color: '#475569', cursor: 'pointer', backgroundColor: '#fff' }}>
                    <option value="">Todas as Vagas</option>
                    {vagasDisponiveis.map(vaga => <option key={vaga} value={vaga}>{vaga}</option>)}
                </select>
            </div>
            
            <div style={{ flex: '1 1 150px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', color: '#475569', textTransform: 'uppercase', marginBottom: '8px' }}>Local</label>
                <select value={filterLocal} onChange={(e) => setFilterLocal(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', color: '#475569', cursor: 'pointer', backgroundColor: '#fff' }}>
                    <option value="">Todos</option>
                    <option value="GTV">GTV</option>
                    <option value="PCI">PCI</option>
                    <option value="SEM_LOCAL">Sem Local (a agendar)</option>
                </select>
            </div>

            <div style={{ flex: '1 1 300px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', color: '#475569', textTransform: 'uppercase', marginBottom: '8px' }}>Período da Inscrição</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input 
                        type="date" 
                        value={filterDataInscricao.inicio} 
                        onChange={(e) => setFilterDataInscricao({...filterDataInscricao, inicio: e.target.value})} 
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', color: '#475569', cursor: 'pointer' }} 
                        title="Data Inicial" 
                    />
                    <span style={{ color: '#94a3b8', fontWeight: '800', fontSize: '12px' }}>ATÉ</span>
                    <input 
                        type="date" 
                        value={filterDataInscricao.fim} 
                        onChange={(e) => setFilterDataInscricao({...filterDataInscricao, fim: e.target.value})} 
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', color: '#475569', cursor: 'pointer' }} 
                        title="Data Final" 
                    />
                </div>
            </div>
            
            <div style={{ flex: '0 0 auto' }}>
                <button onClick={exportarExcel} style={{ backgroundColor: '#10b981', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', height: '40px' }}><Download size={18} /> Exportar XLSX</button>
                {/*<button 
                    onClick={async () => {
                        try {
                            const token = localStorage.getItem('authToken');
                            const res = await fetch('/api/processo-seletivo/forcar-reprovacao-vencidos', {
                                headers: { 'Authorization': `Bearer ${token}` }
                            });
                            const data = await res.json();
                            alert(data.message);
                            window.location.reload(); // Recarrega a tela para sumir com os reprovados
                        } catch (error) {
                            alert("Erro ao testar.");
                        }
                    }} 
                    style={{ backgroundColor: '#ef4444', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: '800', cursor: 'pointer' }}
                >
                    🧹 Forçar Limpeza 21 Dias
                </button>*/}
            </div>
        </div>

        {/* AÇÕES EM LOTE UNIFICADAS */}
        {candidatosSelecionados.length > 0 && (
            <div style={{ backgroundColor: '#ffe4e6', border: '2px solid #fbb6c4', padding: '16px', borderRadius: '12px', marginBottom: '24px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px', animation: 'fadeIn 0.3s' }}>
                <span style={{ fontWeight: '900', color: '#be185d', fontSize: '15px' }}>{candidatosSelecionados.length} selecionado(s):</span>
                
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', borderRight: '2px solid #fbb6c4', paddingRight: '16px', flexWrap: 'wrap' }}>
                    <select value={bulkAgendamento.local} onChange={(e) => setBulkAgendamento({...bulkAgendamento, local: e.target.value, data: ''})} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontWeight: '700', minWidth: '150px' }}>
                        <option value="">1º Escolha o Local</option><option value="GTV">GTV</option><option value="PCI">PCI</option>
                    </select>
                    <select value={bulkAgendamento.data} onChange={(e) => setBulkAgendamento({...bulkAgendamento, data: e.target.value})} disabled={!bulkAgendamento.local} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontWeight: '700', minWidth: '200px' }}>
                        <option value="">2º Escolha a Data</option>
                        {diasBulkDisponiveis.map((m) => {
                            const diaSemanaStr = m.toLocaleDateString('pt-BR', { weekday: 'long' });
                            const diaFormatado = diaSemanaStr.charAt(0).toUpperCase() + diaSemanaStr.slice(1);
                            const diaSemanaNum = m.getDay();
                            
                            let horaItemDisplay = '14:00';
                            if (bulkAgendamento.local === 'PCI') {
                                horaItemDisplay = diaSemanaNum === 1 ? '09:00' : '14:00';
                            }

                            return <option key={m.toISOString().split('T')[0]} value={m.toISOString().split('T')[0]}>
                                {diaFormatado}, {m.toLocaleDateString('pt-BR')} às {horaItemDisplay}
                            </option>
                        })}
                    </select>
                    <button onClick={handleBulkAgendamento} style={{ backgroundColor: '#e11d48', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}><CalendarPlus size={18} /> Agendar Lote</button>
                </div>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button onClick={handleBulkToggleContato} style={{ backgroundColor: '#f59e0b', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(245, 158, 11, 0.2)' }}><MessageCircle size={18} /> Contatados</button>
                    <button onClick={handleBulkFaltaRepescagem} style={{ backgroundColor: '#64748b', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(100, 116, 139, 0.2)' }}><RefreshCcw size={18} /> Remarcar</button>
                    <button onClick={handleBulkRegistrarDesistencia} style={{ backgroundColor: '#e11d48', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(225, 29, 72, 0.2)', marginLeft: 'auto' }}><LogOut size={18} /> Desistência Lote</button>
                </div>
            </div>
        )}

        <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e11d48' }}>
                            <th style={{ padding: '16px', width: '40px', textAlign: 'center' }}><input type="checkbox" checked={isAllSelected} onChange={handleSelectAll} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#e11d48' }} /></th>

                            <th onClick={() => requestSort('DATA_INSCRICAO')} style={{ padding: '16px 20px', color: '#475569', fontSize: '13px', fontWeight: '800', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>Data da Inscrição <ArrowUpDown size={14} opacity={sortConfig.key === 'DATA_INSCRICAO' ? 1 : 0.5} /></div>
                            </th>

                            <th onClick={() => requestSort('CANDIDATO_VAGA')} style={{ padding: '16px 20px', color: '#475569', fontSize: '13px', fontWeight: '800', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>Candidato / Vaga <ArrowUpDown size={14} opacity={sortConfig.key === 'CANDIDATO_VAGA' ? 1 : 0.5} /></div>
                            </th>

                            <th style={{ padding: '16px 20px', color: '#475569', fontSize: '13px', fontWeight: '800', whiteSpace: 'nowrap' }}>CPF / Contato</th>

                            <th onClick={() => requestSort('STATUS')} style={{ padding: '16px 20px', color: '#475569', fontSize: '13px', fontWeight: '800', whiteSpace: 'nowrap', textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>Status <ArrowUpDown size={14} opacity={sortConfig.key === 'STATUS' ? 1 : 0.5} /></div>
                            </th>

                            <th onClick={() => requestSort('LOCAL_ENTREVISTA')} style={{ padding: '16px 20px', color: '#475569', fontSize: '13px', fontWeight: '800', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>Local <ArrowUpDown size={14} opacity={sortConfig.key === 'LOCAL_ENTREVISTA' ? 1 : 0.5} /></div>
                            </th>

                            {canAction && <th style={{ padding: '16px 20px', color: '#475569', fontSize: '13px', fontWeight: '800', whiteSpace: 'nowrap', textAlign: 'center' }}>Ação</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {candidatosPaginados.length > 0 ? (
                            candidatosPaginados.map((cand) => {
                                const isChecked = candidatosSelecionados.includes(cand.ID);
                                const isParecer = cand.STATUS_ETAPA === 3;
                                const localVal = isParecer ? cand.LOCAL_ENTREVISTA : (agendamentos[cand.ID]?.local !== undefined ? agendamentos[cand.ID].local : (cand.LOCAL_ENTREVISTA || ''));
                                const dataVal = isParecer ? (cand.DATA_HORA_ENTREVISTA ? cand.DATA_HORA_ENTREVISTA.substring(0,10) : '') : (agendamentos[cand.ID]?.data !== undefined ? agendamentos[cand.ID].data : (cand.DATA_HORA_ENTREVISTA ? cand.DATA_HORA_ENTREVISTA.substring(0,10) : ''));
                                
                                const diasDisponiveis = localVal === 'GTV' ? proximasSextas : proximasSegundasEQuartas;


                                return (
                                <tr key={cand.ID} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: isChecked ? '#ffe4e6' : 'transparent', transition: 'background-color 0.2s' }}>
                                    <td style={{ padding: '16px', textAlign: 'center', verticalAlign: 'middle' }}><input type="checkbox" checked={isChecked} onChange={() => handleSelectCandidate(cand.ID)} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#e11d48' }} /></td>
                                    
                                    <td style={{ padding: '16px 20px' }}><span style={{ fontWeight: '700', color: '#0f172a' }}>{cand.DATA_INSCRICAO ? new Date(cand.DATA_INSCRICAO).toLocaleDateString('pt-BR') : '-'}</span></td>

                                    <td style={{ padding: '16px 20px', whiteSpace: 'nowrap' }}><strong style={{ color: '#0f172a', fontSize: '14px' }}>{cand.NOME_COMPLETO}</strong><br/><span style={{ color: '#e11d48', fontSize: '13px', fontWeight: '800' }}>{cand.VAGA_SELECIONADA || '-'}</span></td>
                                    
                                    <td style={{ padding: '16px 20px', color: '#475569', fontSize: '13px', whiteSpace: 'nowrap' }}><strong style={{color: '#0f172a'}}>{cand.CPF.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")}</strong><br/>{cand.CELULAR}</td>
                                    
                                    <td style={{ padding: '16px 20px', textAlign: 'center', verticalAlign: 'middle' }}>
                                        {/* (Lógica de badges de Status permanece idêntica) */}
                                        {isParecer ? (
                                            <span style={{ backgroundColor: '#e0e7ff', color: '#4338ca', border: '1px solid #c7d2fe', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: '800', display: 'inline-block', whiteSpace: 'nowrap' }}>Aguardando Parecer</span>
                                        ) : cand.MOTIVO_REPROVACAO === 'REMARCADO' ? (
                                            <span style={{ backgroundColor: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: '800', display: 'inline-block', whiteSpace: 'nowrap' }}>⚠️ Faltou / Remarcado</span>
                                        ) : cand.MOTIVO_REPROVACAO === 'AGUARDANDO_RETORNO' ? (
                                            <span style={{ backgroundColor: '#fffbeb', color: '#b45309', border: '1px solid #fde68a', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: '800', display: 'inline-block', whiteSpace: 'nowrap' }}>⏳ Aguardando Retorno</span>
                                        ) : (
                                            <span style={{ backgroundColor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', padding: '4px 10px', borderRadius: '9999px', fontSize: '11px', fontWeight: '800', display: 'inline-block', whiteSpace: 'nowrap' }}>Novo Agendamento</span>
                                        )}
                                    </td>

                                    <td style={{ padding: '16px 20px' }}><span style={{ fontWeight: '700', color: '#0f172a' }}>{isParecer ? cand.LOCAL_ENTREVISTA || '-' : '-'}</span></td>
                        

                                    {canAction && (
                                        <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                                            {cand.STATUS_ETAPA === 2 ? (
                                                <>
                                                    <select value={localVal} onChange={(e) => handleAgendamentoChange(cand.ID, 'local', e.target.value)} disabled={isChecked} style={{ width: '88px', padding: '7px 6px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '12px' }}>
                                                        <option value="">Local...</option>
                                                        <option value="GTV">GTV</option>
                                                        <option value="PCI">PCI</option>
                                                    </select>
                                                    <select value={dataVal} onChange={(e) => handleAgendamentoChange(cand.ID, 'data', e.target.value)} disabled={!localVal || isChecked} style={{ width: '140px', padding: '7px 6px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '12px' }}>
                                                        <option value="">Data...</option>
                                                        {diasDisponiveis.map((m) => {
                                                            const diaSemanaStr = m.toLocaleDateString('pt-BR', { weekday: 'long' });
                                                            const diaFormatado = diaSemanaStr.charAt(0).toUpperCase() + diaSemanaStr.slice(1);
                                                            const diaSemanaNum = m.getDay();

                                                            let horaItemDisponivel = '14:00';
                                                            if (localVal === 'PCI') {
                                                                horaItemDisponivel = diaSemanaNum === 1 ? '09:00' : '14:00';
                                                            }

                                                            return <option key={m.toISOString().split('T')[0]} value={m.toISOString().split('T')[0]}>
                                                                {diaFormatado}, {m.toLocaleDateString('pt-BR')} às {horaItemDisponivel}
                                                            </option>
                                                        })}
                                                    </select>
                                                    {/* Primária com rótulo */}
                                                    <button onClick={() => handleSalvarAgendamento(cand.ID)} disabled={isChecked} title="Agendar entrevista" style={{ backgroundColor: isChecked ? '#cbd5e1' : '#e11d48', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: isChecked ? 'not-allowed' : 'pointer', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}><CalendarPlus size={15} /> Agendar</button>
                                                    {/* Secundárias só-ícone (tooltip) */}
                                                    <button onClick={() => handleToggleContato(cand.ID, cand.MOTIVO_REPROVACAO !== 'AGUARDANDO_RETORNO')} disabled={isChecked} title={cand.MOTIVO_REPROVACAO === 'AGUARDANDO_RETORNO' ? 'Aguardando Retorno (clique para remover)' : 'Marcar como Aguardando Retorno'} style={{ width: '36px', height: '36px', backgroundColor: cand.MOTIVO_REPROVACAO === 'AGUARDANDO_RETORNO' ? '#f59e0b' : '#fff', color: cand.MOTIVO_REPROVACAO === 'AGUARDANDO_RETORNO' ? '#fff' : '#64748b', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: isChecked ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><MessageCircle size={16} /></button>
                                                    <button onClick={() => handleRegistrarDesistencia(cand.ID)} disabled={isChecked} title="Registrar desistência (move para Desistentes)" style={{ width: '36px', height: '36px', backgroundColor: '#fff', color: '#e11d48', border: '1px solid #fbb6c4', borderRadius: '8px', cursor: isChecked ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><LogOut size={16} /></button>
                                                </>
                                            ) : (
                                                <>
                                                    {/* Primária com rótulo */}
                                                    <button onClick={() => abrirModalAprovacao(cand)} title="Aprovar entrevista" style={{ backgroundColor: '#059669', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}><CheckCircle size={15} /> Aprovar</button>
                                                    {/* Secundárias só-ícone (tooltip) */}
                                                    <button onClick={() => handleReprovarEntrevista(cand.ID)} title="Reprovar candidato" style={{ width: '36px', height: '36px', backgroundColor: '#fff', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><XCircle size={16} /></button>
                                                    <button onClick={() => handleFaltaRepescagem(cand.ID)} title="Faltou / Remarcar (volta para a fila)" style={{ width: '36px', height: '36px', backgroundColor: '#fff', color: '#b45309', border: '1px solid #fde68a', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><RefreshCcw size={16} /></button>
                                                </>
                                            )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                                );
                            })
                        ) : <tr><td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Nenhum candidato na fila.</td></tr>}
                    </tbody>
                </table>
            </div>

            {/* Paginação (50 por página) */}
            {totalPaginas > 1 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                    <span style={{ fontSize: '14px', color: '#64748b' }}>
                        Mostrando {indexPrimeiro + 1} a {Math.min(indexPrimeiro + ITENS_POR_PAGINA, candidatosFiltrados.length)} de {candidatosFiltrados.length}
                    </span>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button onClick={() => setPaginaAtual(p => Math.max(p - 1, 1))} disabled={paginaAtual === 1} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: paginaAtual === 1 ? '#f1f5f9' : '#fff', color: paginaAtual === 1 ? '#cbd5e1' : '#0f172a', cursor: paginaAtual === 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}>
                            <ArrowLeft size={18} />
                        </button>
                        <span style={{ padding: '8px 16px', fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>Página {paginaAtual} de {totalPaginas}</span>
                        <button onClick={() => setPaginaAtual(p => Math.min(p + 1, totalPaginas))} disabled={paginaAtual === totalPaginas} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: paginaAtual === totalPaginas ? '#f1f5f9' : '#fff', color: paginaAtual === totalPaginas ? '#cbd5e1' : '#0f172a', cursor: paginaAtual === totalPaginas ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', transform: 'rotate(180deg)' }}>
                            <ArrowLeft size={18} />
                        </button>
                    </div>
                </div>
            )}
        </div>

        {/* MODAL DE APROVAÇÃO E SUPER POP-UP DE AUTOMAÇÃO */}
        {isModalOpen && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
                <div style={{ backgroundColor: '#fff', padding: '32px', borderRadius: '16px', width: '100%', maxWidth: '750px', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
                    
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', justifyContent: 'center' }}>
                        <div style={{ width: '40px', height: '6px', borderRadius: '4px', backgroundColor: modalStep === 1 ? '#e11d48' : '#e2e8f0', transition: 'all 0.3s' }} />
                        <div style={{ width: '40px', height: '6px', borderRadius: '4px', backgroundColor: modalStep === 2 ? '#e11d48' : '#e2e8f0', transition: 'all 0.3s' }} />
                    </div>

                    {modalStep === 1 && (
                        <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
                            <h3 style={{ margin: '0 0 8px 0', fontSize: '22px', fontWeight: '900', color: '#0f172a' }}>Passo 1: Confirmação de Vaga</h3>
                            <p style={{ margin: '0 0 24px 0', color: '#64748b', fontSize: '14px' }}>Confirme os dados para a contratação de <strong style={{color: '#0f172a'}}>{candidatoSelecionado?.NOME_COMPLETO}</strong>.</p>
                            
                            <div style={{ backgroundColor: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
                                <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: '800', color: '#3b82f6' }}>📝 Dados Pessoais Editáveis</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                                    <div><label style={labelStyleModal}>Nome</label><input type="text" value={vagaData.nome} onChange={e => setVagaData({...vagaData, nome: e.target.value})} style={inputStyleModal} /></div>
                                    <div><label style={labelStyleModal}>CPF</label><input type="text" value={vagaData.cpf} onChange={e => setVagaData({...vagaData, cpf: e.target.value})} style={inputStyleModal} maxLength="14" /></div>
                                    <div><label style={labelStyleModal}>E-mail</label><input type="email" value={vagaData.email} onChange={e => setVagaData({...vagaData, email: e.target.value})} style={inputStyleModal} /></div>
                                    <div><label style={labelStyleModal}>Celular</label><input type="text" value={vagaData.celular} onChange={e => setVagaData({...vagaData, celular: e.target.value})} style={inputStyleModal} /></div>
                                </div>
                            </div>

                            <div style={{ backgroundColor: '#ffe4e6', padding: '16px', borderRadius: '12px', border: '1px solid #ffe4e6', marginBottom: '32px' }}>
                                <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: '800', color: '#e11d48' }}>💼 Detalhes da Contratação</h4>
                                
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                                    <div>
                                        <label style={labelStyleModal}>Loja de Atuação</label>
                                        <select
                                            value={vagaData.loja}
                                            onChange={e => setVagaData({...vagaData, loja: e.target.value})}
                                            disabled={isGTV}
                                            style={{...inputStyleModal, border: '2px solid #e11d48', backgroundColor: isGTV ? '#f1f5f9' : '#fff', cursor: isGTV ? 'not-allowed' : 'auto'}}
                                        >
                                            <option value="">Selecione a Loja...</option><option value="ANV">ANV</option><option value="BVV">BVV</option><option value="FER">FER</option><option value="GTV">GTV</option><option value="MAL">MAL</option><option value="MOX">MOX</option><option value="PCI">PCI</option><option value="PCS">PCS</option><option value="RDP">RDP</option><option value="SED">SED</option><option value="SVV">SVV</option><option value="VEL">VEL</option><option value="VSH">VSH</option>
                                        </select>
                                    </div>

                                    <div><label style={labelStyleModal}>1. Grupo do Cargo</label><select value={vagaData.grupo} onChange={e => setVagaData({...vagaData, grupo: e.target.value, cargo: ''})} style={inputStyleModal}><option value="">Selecione...</option>{gruposUnicos.map((g, i) => <option key={i} value={g}>{g}</option>)}</select></div>
                                    <div><label style={labelStyleModal}>2. Cargo Contratado</label><select value={vagaData.cargo} onChange={e => setVagaData({...vagaData, cargo: e.target.value})} disabled={!vagaData.grupo} style={{...inputStyleModal, backgroundColor: vagaData.grupo ? '#fff' : '#f1f5f9'}}><option value="">Selecione...</option>{cargosFiltrados.map((c, i) => <option key={i} value={c}>{c}</option>)}</select></div>
                                    <div><label style={labelStyleModal}>Tipo de Escala</label><select value={vagaData.tipo_escala} onChange={e => setVagaData({...vagaData, tipo_escala: e.target.value, nome_escala: '', folga: ''})} style={inputStyleModal}><option value="">Selecione...</option><option value="12X36">12x36</option><option value="5X2">5x2</option><option value="PARTIME">PARTIME</option></select></div>
                                    <div><label style={labelStyleModal}>Horário da Escala</label><select value={vagaData.nome_escala} onChange={e => setVagaData({...vagaData, nome_escala: e.target.value})} disabled={!vagaData.tipo_escala} style={{...inputStyleModal, backgroundColor: vagaData.tipo_escala ? '#fff' : '#f1f5f9'}}><option value="">{vagaData.tipo_escala ? 'Selecione o Horário...' : 'Selecione o Tipo 1º'}</option>{horariosEscalaUnicos.map((h, i) => <option key={i} value={h}>{h}</option>)}</select></div>
                                    <div><label style={labelStyleModal}>Dia(s) de Folga</label><select value={vagaData.folga} onChange={e => setVagaData({...vagaData, folga: e.target.value})} disabled={!vagaData.tipo_escala} style={{...inputStyleModal, backgroundColor: vagaData.tipo_escala ? '#fff' : '#f1f5f9'}}><option value="">{vagaData.tipo_escala ? 'Selecione a Folga...' : 'Selecione o Tipo 1º'}</option>{renderFolgaOptions()}</select></div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                                <button onClick={() => setIsModalOpen(false)} style={{ padding: '12px 24px', borderRadius: '8px', border: '2px solid #e2e8f0', backgroundColor: '#fff', color: '#64748b', fontWeight: '800', cursor: 'pointer' }}>Cancelar</button>
                                
                                <button onClick={handleAvancarParaExame} style={{ padding: '12px 24px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 6px rgba(16, 185, 129, 0.3)' }}>
                                    {(isGTV || vagaData.cargo?.toUpperCase() === 'ESTOQUISTA') ? (
                                        <><CheckCircle size={18} /> Concluir (Pular para Integração)</>
                                    ) : (
                                        <>Avançar para Exame <ArrowRight size={18} /></>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {modalStep === 2 && (
                        <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
                            <h3 style={{ margin: '0 0 8px 0', fontSize: '22px', fontWeight: '900', color: '#0f172a' }}>Passo 2: Exame Admissional</h3>
                            <p style={{ margin: '0 0 24px 0', color: '#64748b', fontSize: '14px' }}>Selecione a data para agendar o exame e a integração (o horário é fixo às 08:00).</p>
                            
                            <div style={{ backgroundColor: '#f8fafc', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', marginBottom: '32px' }}>
                                <div style={{ marginBottom: '24px', maxWidth: '300px' }}>
                                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '800', color: '#e11d48', marginBottom: '8px' }}>
                                        1. Escolha a Data
                                    </label>
                                    <input
                                        type="date"
                                        value={vagaData.data_exame}
                                        min={dataMinimaFormatada}
                                        onChange={(e) => setVagaData({...vagaData, data_exame: e.target.value, hora_exame: '08:00'})}
                                        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '2px solid #fbb6c4', outline: 'none', fontSize: '15px', fontWeight: '700', color: '#0f172a', backgroundColor: '#fff', cursor: 'pointer' }}
                                    />
                                </div>

                                {vagaData.data_exame && (
                                    <div style={{ animation: 'fadeIn 0.4s ease-out', backgroundColor: '#eff6ff', padding: '16px', borderRadius: '8px', borderLeft: '4px solid #3b82f6' }}>
                                        <p style={{ margin: '0 0 8px 0', color: '#1e3a8a', fontWeight: '800', fontSize: '16px' }}>⏰ Horário Fixo: 08:00</p>
                                        <p style={{ margin: 0, color: '#334155', fontSize: '14px' }}>O candidato será instruído a realizar o exame às 08h e, caso aprovado, permanecerá para a integração no mesmo dia.</p>
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <button onClick={() => setModalStep(1)} style={{ padding: '12px 24px', borderRadius: '8px', border: 'none', backgroundColor: 'transparent', color: '#64748b', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}><ArrowLeft size={18} /> Voltar aos Dados</button>
                                <button 
                                    onClick={() => handleAprovarSubmeter(false)} 
                                    disabled={!vagaData.data_exame}
                                    style={{ padding: '12px 24px', borderRadius: '8px', border: 'none', background: vagaData.data_exame ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : '#94a3b8', color: '#fff', fontWeight: '800', cursor: vagaData.data_exame ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.3s', boxShadow: vagaData.data_exame ? '0 4px 6px rgba(16, 185, 129, 0.3)' : 'none' }}
                                >
                                    <CheckCircle size={18} /> Concluir e Enviar E-mail
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )}
        
        {automacaoModal.isOpen && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 99999, padding: '20px', animation: 'fadeIn 0.2s' }}>
                <div style={{ backgroundColor: '#fff', borderRadius: '24px', width: '100%', maxWidth: '700px', padding: '48px 32px', textAlign: 'center', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}>
                    
                    <div style={{ backgroundColor: '#fef3c7', width: '96px', height: '96px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px auto' }}>
                        <MessageCircle size={48} color="#d97706" />
                    </div>
                    
                    <h2 style={{ fontSize: '32px', fontWeight: '900', color: '#0f172a', marginBottom: '16px' }}>
                        {automacaoModal.titulo}
                    </h2>
                    
                    <p style={{ fontSize: '18px', color: '#475569', marginBottom: '40px', lineHeight: '1.6' }}>
                        {automacaoModal.mensagem}
                    </p>
                    
                    <div style={{ display: 'flex', gap: '20px', justifyContent: 'center' }}>
                        <button 
                            onClick={() => automacaoModal.onCancel()} 
                            style={{ padding: '16px 24px', fontSize: '18px', borderRadius: '12px', border: '2px solid #e2e8f0', backgroundColor: '#f8fafc', color: '#64748b', fontWeight: '800', cursor: 'pointer', transition: 'all 0.2s', flex: 1 }}
                        >
                            Não, apenas salvar
                        </button>
                        
                        <button 
                            onClick={() => automacaoModal.onConfirm()} 
                            style={{ padding: '16px 24px', fontSize: '18px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff', fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.3)', flex: 1 }}
                        >
                            <CheckCircle size={24} /> Sim, Enviar Mensagem
                        </button>
                    </div>

                    <button 
                        onClick={() => setAutomacaoModal({ ...automacaoModal, isOpen: false })} 
                        style={{ marginTop: '32px', background: 'none', border: 'none', color: '#94a3b8', textDecoration: 'underline', cursor: 'pointer', fontSize: '15px', fontWeight: '600' }}
                    >
                        Cancelar tudo e fechar
                    </button>
                </div>
            </div>
        )}
    </div>
);
};

export default ProcessoSeletivoEntrevistaAgendamento;